import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import PageHeader from "../../../app/layout/PageHeader";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../api/apiClient";
import OrganisationPeopleForm, { OrganisationPerson } from "./OrganisationPeopleForm";
import "./tenant-administration.css";

export default function OrganisationPeoplePage({ tenantId: suppliedTenantId, embedded = false, onHeaderActionChange }: {
  tenantId?: number; embedded?: boolean; onHeaderActionChange?: (action: ReactNode | null) => void;
}) {
  const { selectedTenant } = useAuth();
  const tenantId = suppliedTenantId ?? selectedTenant?.tenantId;
  const [people, setPeople] = useState<OrganisationPerson[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState<OrganisationPerson | null | undefined>(undefined);
  const headerAction = useMemo(() => tenantId ? <button type="button"
    className={`icon-button-solid icon-button-solid--${editing === undefined ? "success" : "danger"}`}
    aria-label={editing === undefined ? "Add person" : "Cancel editing"} title={editing === undefined ? "Add person" : "Cancel editing"}
    onClick={() => setEditing(editing === undefined ? null : undefined)}>
    {editing === undefined ? <AddIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
  </button> : null, [tenantId, editing]);
  useEffect(() => {
    if (!onHeaderActionChange) return;
    onHeaderActionChange(headerAction);
    return () => onHeaderActionChange(null);
  }, [headerAction, onHeaderActionChange]);
  const refresh = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true); setError(false);
    try { setPeople(await api<OrganisationPerson[]>(`/api/tenant-administration/${tenantId}/people`)); }
    catch { setError(true); }
    finally { setLoading(false); }
  }, [tenantId]);
  useEffect(() => { setEditing(undefined); setPeople([]); void refresh(); }, [refresh]);
  const visible = people.filter(person => (filter === "all" || (filter === "login" ? person.canLogIn : !person.canLogIn)) &&
    `${person.displayName} ${person.email} ${person.serviceNames.join(" ")}`.toLowerCase().includes(search.trim().toLowerCase()));
  return <div className="tenant-admin-page">
    {!embedded && <PageHeader title="Organisation People" subtitle="Manage people, enrolled services and application access." actions={headerAction} />}
    {!tenantId ? <div className="card tenant-admin-empty">Select an active organisation to manage its people.</div>
      : editing !== undefined ? <OrganisationPeopleForm key={`${tenantId}-${editing?.partyId ?? editing?.userId ?? "new"}`} tenantId={tenantId}
        existing={editing ?? undefined} onCancel={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); void refresh(); }} />
      : <div className="card organisation-people-list">
        <div className="organisation-people-toolbar">
          <input className="form-input" type="search" aria-label="Search people" placeholder="Search people..." value={search} onChange={event => setSearch(event.target.value)} />
          <select className="form-input" aria-label="Filter people" value={filter} onChange={event => setFilter(event.target.value)}>
            <option value="all">All people</option><option value="login">With application access</option><option value="members">Without application access</option>
          </select>
        </div>
        {loading ? <p className="muted">Loading people...</p> : error ? <p role="alert">Unable to load people. <button type="button" className="button-link" onClick={() => { void refresh(); }}>Try again</button></p>
          : visible.length === 0 ? <p className="muted">No people found.</p> : <div className="organisation-members-list" role="list">
            {visible.map(person => <div className="organisation-member-row" role="listitem" key={person.partyId ? `party-${person.partyId}` : `user-${person.userId}`}>
              <div className="organisation-member-main"><span className="organisation-member-icon" aria-hidden="true"><PersonIcon /></span>
                <div className="organisation-member-copy"><div className="organisation-member-name">{person.displayName}</div>
                  {person.serviceNames.length > 0 && <div className="organisation-member-meta">{person.serviceNames.join(", ")}</div>}
                  {person.email && <div className="organisation-member-meta">{person.email}</div>}
                  <span className={`organisation-login-status${person.canLogIn ? " organisation-login-status--enabled" : ""}`}>{person.canLogIn ? "Application access enabled" : "No application access"}</span>
                </div>
              </div>
              <button type="button" className="icon-button" title={`Edit ${person.displayName}`} aria-label={`Edit ${person.displayName}`} onClick={() => setEditing(person)}><EditIcon fontSize="small" /></button>
            </div>)}
          </div>}
      </div>}
  </div>;
}
